import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { supabase } from '../lib/supabase';
import { destinoDelExitoPorRedirect } from '../lib/checkoutPlans';
import { marcarOnboardingPendiente } from '../lib/cuestionarioObligatorio';
import { recordarPagoConMarcaPendiente } from '../lib/pagoConMarcaPendiente';
import { ONBOARDING_V2 } from '../components/onboarding/onboardingV2Routes';
import AddToHomescreenPrompt from '../components/AddToHomescreenPrompt';
import { useDeviceDetection } from '../hooks/useDeviceDetection';

const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [nameShort, setNameShort] = useState<string>('');
  const [isMale, setIsMale] = useState<boolean>(true);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const detection = useDeviceDetection();

  // A dónde sigue "Continuar": los planes Pro y Ultra pasan primero por la pantalla de
  // la llamada de capacitación incluida
  // (ADR-2026-08-25-llamada-capacitacion-ultra-post-pago); el resto, al home de
  // siempre. El plan se consulta AL CLICK y no al montar: a esta pantalla se
  // llega volviendo del redirect (Stripe/MP) y el webhook que activa el plan
  // puede tardar unos segundos — para cuando el usuario termina de leer y toca,
  // hay muchas más chances de que ya esté escrito. Best-effort: si algo falla,
  // al home como siempre.
  const irAlDestino = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: prof } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        if (prof?.id_company) {
          // `rubro` viaja en la MISMA consulta que el plan: es lo que dice si a esta
          // empresa le falta el cuestionario (el alta por link la crea con el centinela
          // para poder cobrar en un paso). Acá no hay otra forma de saberlo: Stripe vuelve
          // siempre a la misma `success_url` global, así que el llamador no puede mandar
          // nada. Ver ADR-2026-09-22-el-destino-post-pago-sobrevive-la-cadena.
          // Con `debe_onboarding_marca`: quien empieza a pagar y lo debe va al onboarding de
          // marca, con la llamada al final (ADR-2026-10-31-onboarding-corto-para-la-prueba).
          const { data: company } = await supabase
            .from('company_profiles')
            .select('plan, rubro, debe_onboarding_marca')
            .eq('id_company', prof.id_company)
            .single();
          const fila = company as {
            plan?: string | null;
            rubro?: string | null;
            debe_onboarding_marca?: boolean | null;
          } | null;
          const destino = destinoDelExitoPorRedirect({
            plan: fila?.plan,
            rubro: fila?.rubro,
            // Sin fila no se deduce nada: mejor entrar como siempre que meter en un
            // cuestionario a quien no le corresponde. El rebote de App lo agarra en la
            // próxima carga completa.
            pudoLeerse: fila != null,
            debeOnboardingMarca: fila?.debe_onboarding_marca,
          });
          // El guardia la devuelve ahí si sale a la app antes de terminarlo.
          if (destino === ONBOARDING_V2.marcaSetup) {
            marcarOnboardingPendiente('marca');
            recordarPagoConMarcaPendiente(user.id);
          }
          if (destino) {
            navigate(destino, { state: { postPago: true, plan: fila?.plan ?? undefined } });
            return;
          }
        }
      }
    } catch {}
    navigate('/');
  }, [navigate]);

  const handleContinue = useCallback(() => {
    if (detection.canInstall && !detection.isStandalone) {
      setShowInstallPrompt(true);
    } else {
      void irAlDestino();
    }
  }, [detection.canInstall, detection.isStandalone, irAlDestino]);

  const handleInstallDismiss = useCallback(() => {
    setShowInstallPrompt(false);
    void irAlDestino();
  }, [irAlDestino]);

  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('name_short, name_sex_male')
          .eq('id', user.id)
          .single();
        if (profile) {
          setNameShort((profile as any).name_short || '');
          const male = (profile as any)?.name_sex_male === null ? true : !!(profile as any)?.name_sex_male;
          setIsMale(male);
        }
      } catch {}
    })();
  }, []);

  return (
    <div className="fixed inset-0 z-40 md:absolute md:inset-0 md:z-auto w-full md:flex md:items-center md:justify-center bg-surface-card md:bg-transparent overflow-y-auto">
      <div className="relative bg-surface-card w-full min-h-full md:min-h-0 md:h-auto px-4 md:px-8 py-10 pb-28 md:py-12 md:max-w-4xl md:mx-auto md:rounded-lg md:border md:border-neutral-200 flex items-center justify-center">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto mb-4 w-24 h-24">
            <DotLottieReact
              src="/images/lottie_animations/Success.lottie"
              autoplay
              style={{ width: '100%', height: '100%' }}
            />
          </div>
          <h1 className="text-[17px] font-semibold text-brand-black/90 mb-2">Subscripción exitosa.</h1>
          <p className="text-[15px] text-neutral-600">
            {`${nameShort ? `${nameShort}, ` : ''}${isMale ? 'bienvenido' : 'bienvenida'} a la revolución de la IA.`}
          </p>

          {/* Desktop inline helper + button */}
          <div className="hidden md:block mt-8">
            <p className="text-[13px] text-neutral-400 mb-3">Ante cualquier duda te podes comunicar con nostros yendo a la pestaña de Ayuda.</p>
            <button
              onClick={handleContinue}
              className="inline-flex items-center justify-center h-10 px-5 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
            >
              Continuar
            </button>
          </div>
        </div>
      </div>
      {/* Mobile fixed bottom actions. El fondo blanco de la barra debe pintar a
          través de env(safe-area-inset-bottom) (zona del home indicator en PWA
          standalone / iOS con notch); sin esto asoma el #F2F2F7 del <html>. */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 bg-surface-card px-4 pt-4 border-t border-neutral-200"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        <p className="text-[13px] text-neutral-400 mb-2 text-center">Ante cualquier duda te podes comunicar con nostros yendo a la pestaña de ayuda.</p>
        <button
          onClick={handleContinue}
          className="w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
        >
          Continuar
        </button>
      </div>
      {showInstallPrompt && (
        <AddToHomescreenPrompt
          delayMs={0}
          forceShow
          origen="suscripcion_exitosa"
          onDismiss={handleInstallDismiss}
          onInstall={handleInstallDismiss}
        />
      )}
    </div>
  );
};

export default SubscriptionSuccess;
